import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table, TableBody, TableCell, TableHeader, TableRow,
} from '@/components/shadcn/table'
import { TableCard } from '@/components/common/table/TableCard'
import { PaginationFooter } from '@/components/common/table/PaginationFooter'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import { SortableTableHead } from '@/components/common/SortableTableHead'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterSearch } from '@/components/common/filters/FilterSearch'
import { useGetApiCompaniesPaginated } from '@/api/generated/administrator-user/administrator-user'
import { OrderType } from '@/api/model/orderType'

const DEFAULT_PAGE_SIZE = 20

export function CompaniesTable() {
  const { t } = useTranslation('super-admin-companies')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState<string | undefined>()
  const [sort, setSort] = useState<string | undefined>('name')
  const [order, setOrder] = useState<typeof OrderType[keyof typeof OrderType] | undefined>(OrderType.Ascending)

  const handleSort = (field: string | undefined, ord: typeof OrderType[keyof typeof OrderType] | undefined) => {
    setSort(field)
    setOrder(ord)
    setPage(1)
  }

  const { data, isLoading, isError } = useGetApiCompaniesPaginated({
    Offset: (page - 1) * pageSize,
    Limit: pageSize,
    Sort: sort,
    Order: order,
    Search: search,
  })
  const items = data?.data?.items ?? []
  const totalCount = data?.data?.totalCount ?? 0

  return (
    <>
      <FilterRow>
        <FilterSearch
          searchLabel={t('filters.search')}
          search={search}
          onChange={(value) => { setSearch(value || undefined); setPage(1) }}
        />
      </FilterRow>

      <TableCard>
        <div className="flex-1 overflow-auto scrollbar-custom relative">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
              <TableRow>
                <SortableTableHead field="id" currentSort={sort} currentOrder={order} onSort={handleSort} className="w-[100px]">{t('columns.id')}</SortableTableHead>
                <SortableTableHead field="customerNumber" currentSort={sort} currentOrder={order} onSort={handleSort}>{t('columns.customerNumber')}</SortableTableHead>
                <SortableTableHead field="name" currentSort={sort} currentOrder={order} onSort={handleSort}>{t('columns.name')}</SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <LoaderTable isLoading={isLoading} isError={isError} isEmpty={items.length === 0} colSpan={3} emptyMessage={t('empty')}>
                {items.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell className="text-muted-foreground">{company.id}</TableCell>
                    <TableCell className="w-[160px] font-medium">{company.customerNumber}</TableCell>
                    <TableCell>{company.name ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </LoaderTable>
            </TableBody>
          </Table>
        </div>
        <PaginationFooter
          offset={(page - 1) * pageSize}
          limit={pageSize}
          totalCount={totalCount}
          onOffsetChange={(offset) => setPage(Math.floor(offset / pageSize) + 1)}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
        />
      </TableCard>
    </>
  )
}
